import {
  AI_RECEPTIONIST_SOLUTION_V1,
  validateSolutionBlueprint,
  type SolutionBlueprintV1,
} from "@clickdialog/solution-contract";

export type SolutionNodeKind =
  | "channel"
  | "conversation"
  | "agent"
  | "business"
  | "knowledge"
  | "automation"
  | "human";

export type SolutionNodeKey = string;

export interface SolutionNodeDefinition {
  key: SolutionNodeKey;
  label: string;
  eyebrow: string;
  kind: SolutionNodeKind;
  description: string;
  owner?: string | null;
  capabilityKey?: string | null;
  channelKey?: string | null;
  x: number;
  y: number;
}

export interface SolutionEdgeDefinition {
  key: string;
  from: SolutionNodeKey;
  to: SolutionNodeKey;
}

export type ExecutionEventOrigin = "runtime" | "simulation";
export type ExecutionEventPhase = "started" | "completed" | "failed" | "handoff";

export interface PulseExecutionEvent {
  eventId: string;
  correlationId: string;
  origin: ExecutionEventOrigin;
  phase: ExecutionEventPhase;
  nodeKey: SolutionNodeKey;
  organizationId?: string;
  source?: string;
  eventType?: string;
  durationMs?: number;
  edgeKey?: string;
  label: string;
  detail: string;
  offsetMs?: number;
  occurredAt?: string;
}

function solutionNodeKey(value: string): SolutionNodeKey {
  if (!/^[a-z0-9](?:[a-z0-9._:-]{0,95})$/.test(value)) {
    throw new Error("solution_studio_node_key_invalid:" + value);
  }
  return value;
}

const blueprintValidation = validateSolutionBlueprint(
  AI_RECEPTIONIST_SOLUTION_V1,
);
if (!blueprintValidation.valid) {
  throw new Error(
    "solution_studio_blueprint_invalid:" +
      blueprintValidation.issues.map((entry) => entry.code).join(","),
  );
}

export const SOLUTION_BLUEPRINT = AI_RECEPTIONIST_SOLUTION_V1;

export function projectSolutionBlueprint(
  blueprint: SolutionBlueprintV1,
): {
  readonly nodes: readonly SolutionNodeDefinition[];
  readonly edges: readonly SolutionEdgeDefinition[];
} {
  const validation = validateSolutionBlueprint(blueprint);
  if (!validation.valid) {
    throw new Error(
      "solution_studio_blueprint_invalid:" +
        validation.issues.map((entry) => entry.code).join(","),
    );
  }

  const nodes = blueprint.nodes.map((node) => {
    if (!node.layout) {
      throw new Error("solution_studio_layout_missing:" + node.key);
    }
    return Object.freeze({
      key: solutionNodeKey(node.key),
      label: node.label,
      eyebrow: node.eyebrow,
      kind: node.kind,
      description: node.description,
      owner: node.owner ?? null,
      capabilityKey: node.capabilityKey ?? null,
      channelKey: node.channelKey ?? null,
      x: node.layout.x,
      y: node.layout.y,
    });
  });

  const edges = blueprint.edges.map((edge) => Object.freeze({
    key: edge.key,
    from: solutionNodeKey(edge.from),
    to: solutionNodeKey(edge.to),
  }));

  return Object.freeze({
    nodes: Object.freeze(nodes),
    edges: Object.freeze(edges),
  });
}

export function updateSolutionLifecycle(
  blueprint: SolutionBlueprintV1,
  lifecycle: "draft" | "candidate",
): SolutionBlueprintV1 {
  const updated: SolutionBlueprintV1 = Object.freeze({
    ...blueprint,
    lifecycle,
  });
  const validation = validateSolutionBlueprint(updated);
  if (!validation.valid) {
    throw new Error(
      "solution_studio_lifecycle_update_invalid:" +
        validation.issues.map((entry) => entry.code).join(","),
    );
  }
  return updated;
}

export function updateSolutionNodeLayout(
  blueprint: SolutionBlueprintV1,
  nodeKey: SolutionNodeKey,
  layout: { readonly x: number; readonly y: number },
): SolutionBlueprintV1 {
  if (
    !Number.isFinite(layout.x) ||
    !Number.isFinite(layout.y) ||
    layout.x < 0 ||
    layout.x > 100 ||
    layout.y < 0 ||
    layout.y > 100
  ) {
    throw new Error("solution_studio_layout_out_of_bounds:" + nodeKey);
  }

  let found = false;
  const nodes = blueprint.nodes.map((node) => {
    if (node.key !== nodeKey) return node;
    found = true;
    return Object.freeze({
      ...node,
      layout: Object.freeze({
        x: Math.round(layout.x * 10) / 10,
        y: Math.round(layout.y * 10) / 10,
      }),
    });
  });
  if (!found) {
    throw new Error("solution_studio_node_not_found:" + nodeKey);
  }

  const updated: SolutionBlueprintV1 = Object.freeze({
    ...blueprint,
    nodes: Object.freeze(nodes),
  });
  const validation = validateSolutionBlueprint(updated);
  if (!validation.valid) {
    throw new Error(
      "solution_studio_layout_update_invalid:" +
        validation.issues.map((entry) => entry.code).join(","),
    );
  }
  return updated;
}

function validateGraphMutation(
  blueprint: SolutionBlueprintV1,
  code: string,
): SolutionBlueprintV1 {
  const validation = validateSolutionBlueprint(blueprint);
  if (!validation.valid) {
    throw new Error(
      code + ":" +
        validation.issues.map((entry) => entry.code).join(","),
    );
  }
  return blueprint;
}

export function addSolutionEdge(
  blueprint: SolutionBlueprintV1,
  from: SolutionNodeKey,
  to: SolutionNodeKey,
): SolutionBlueprintV1 {
  if (from === to) {
    throw new Error("solution_studio_edge_self_forbidden:" + from);
  }
  const nodeKeys = new Set(blueprint.nodes.map((node) => node.key));
  if (!nodeKeys.has(from) || !nodeKeys.has(to)) {
    throw new Error("solution_studio_edge_node_missing:" + from + ":" + to);
  }
  if (blueprint.edges.some((edge) => edge.from === from && edge.to === to)) {
    throw new Error("solution_studio_edge_duplicate:" + from + ":" + to);
  }

  const existingKeys = new Set(blueprint.edges.map((edge) => edge.key));
  const base = "edge-" + from + "-" + to;
  let key = base;
  let suffix = 2;
  while (existingKeys.has(key)) {
    key = base + "-" + suffix++;
  }

  return validateGraphMutation(
    Object.freeze({
      ...blueprint,
      edges: Object.freeze([
        ...blueprint.edges,
        Object.freeze({ key, from, to }),
      ]),
    }),
    "solution_studio_edge_add_invalid",
  );
}

export function removeSolutionEdge(
  blueprint: SolutionBlueprintV1,
  edgeKey: string,
): SolutionBlueprintV1 {
  const edge = blueprint.edges.find((candidate) => candidate.key === edgeKey);
  if (!edge) {
    throw new Error("solution_studio_edge_not_found:" + edgeKey);
  }

  return validateGraphMutation(
    Object.freeze({
      ...blueprint,
      edges: Object.freeze(
        blueprint.edges.filter((candidate) => candidate.key !== edgeKey),
      ),
    }),
    "solution_studio_edge_remove_invalid",
  );
}

function uniqueKeys(values: readonly string[]): readonly string[] {
  return Object.freeze([...new Set(values)]);
}

export function removeSolutionNode(
  blueprint: SolutionBlueprintV1,
  nodeKey: SolutionNodeKey,
): SolutionBlueprintV1 {
  const target = blueprint.nodes.find((node) => node.key === nodeKey);
  if (!target) {
    throw new Error("solution_studio_node_not_found:" + nodeKey);
  }

  const nodes = blueprint.nodes.filter((node) => node.key !== nodeKey);
  const edges = blueprint.edges.filter(
    (edge) => edge.from !== nodeKey && edge.to !== nodeKey,
  );

  const capabilityStillUsed = target.capabilityKey == null ||
    nodes.some((node) => node.capabilityKey === target.capabilityKey);
  const channelStillUsed = target.channelKey == null ||
    nodes.some((node) => node.channelKey === target.channelKey);

  const requirements = Object.freeze({
    capabilities: Object.freeze(
      blueprint.requirements.capabilities.filter(
        (key) => capabilityStillUsed || key !== target.capabilityKey,
      ),
    ),
    channels: Object.freeze(
      blueprint.requirements.channels.filter(
        (key) => channelStillUsed || key !== target.channelKey,
      ),
    ),
    humanControl: nodes.some((node) => node.kind === "human")
      ? blueprint.requirements.humanControl
      : false,
  });

  return validateGraphMutation(
    Object.freeze({
      ...blueprint,
      nodes: Object.freeze(nodes),
      edges: Object.freeze(edges),
      requirements,
    }),
    "solution_studio_node_remove_invalid",
  );
}

export function restoreSolutionNodeFromTemplate(
  blueprint: SolutionBlueprintV1,
  nodeKey: SolutionNodeKey,
): SolutionBlueprintV1 {
  if (blueprint.nodes.some((node) => node.key === nodeKey)) {
    throw new Error("solution_studio_node_duplicate:" + nodeKey);
  }

  const templateNode = SOLUTION_BLUEPRINT.nodes.find(
    (node) => node.key === nodeKey,
  );
  if (!templateNode) {
    throw new Error("solution_studio_template_node_missing:" + nodeKey);
  }

  const canonicalOrder = new Map(
    SOLUTION_BLUEPRINT.nodes.map((node, index) => [node.key, index]),
  );
  const nodes = [...blueprint.nodes, templateNode].sort(
    (a, b) =>
      (canonicalOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
      (canonicalOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER),
  );
  const nodeKeys = new Set(nodes.map((node) => node.key));
  const edgePairs = new Set(
    blueprint.edges.map((edge) => edge.from + "->" + edge.to),
  );
  const restoredEdges = SOLUTION_BLUEPRINT.edges.filter(
    (edge) =>
      (edge.from === nodeKey || edge.to === nodeKey) &&
      nodeKeys.has(edge.from) &&
      nodeKeys.has(edge.to) &&
      !edgePairs.has(edge.from + "->" + edge.to),
  );

  const capabilities = templateNode.capabilityKey
    ? uniqueKeys([
        ...blueprint.requirements.capabilities,
        templateNode.capabilityKey,
      ])
    : blueprint.requirements.capabilities;
  const channels = templateNode.channelKey
    ? uniqueKeys([
        ...blueprint.requirements.channels,
        templateNode.channelKey,
      ])
    : blueprint.requirements.channels;

  return validateGraphMutation(
    Object.freeze({
      ...blueprint,
      nodes: Object.freeze(nodes),
      edges: Object.freeze([
        ...blueprint.edges,
        ...restoredEdges,
      ]),
      requirements: Object.freeze({
        capabilities,
        channels,
        humanControl: blueprint.requirements.humanControl ||
          templateNode.kind === "human",
      }),
    }),
    "solution_studio_node_restore_invalid",
  );
}

const canonicalProjection = projectSolutionBlueprint(SOLUTION_BLUEPRINT);

export const SOLUTION_NODES: readonly SolutionNodeDefinition[] =
  canonicalProjection.nodes;

export const SOLUTION_EDGES: readonly SolutionEdgeDefinition[] =
  canonicalProjection.edges;

export interface SolutionDemoScenario {
  readonly key: string;
  readonly label: string;
  readonly input: string;
  readonly result: string;
  readonly events: readonly PulseExecutionEvent[];
}

export interface SolutionExecutionSummary {
  readonly eventCount: number;
  readonly nodeCount: number;
  readonly failureCount: number;
  readonly handoffCount: number;
  readonly totalDurationMs: number;
  readonly status: "idle" | "running" | "completed" | "failed" | "handoff";
  readonly resultLabel: string | null;
}

function demoCopyForNode(
  node: SolutionNodeDefinition,
): Readonly<{ label: string; detail: string; durationMs: number }> {
  if (node.channelKey === "voice") {
    return Object.freeze({
      label: "Voice simulé",
      detail: "Une interaction Voice synthétique traverse ce canal.",
      durationMs: 180,
    });
  }
  if (node.channelKey === "meta-whatsapp") {
    return Object.freeze({
      label: "WhatsApp simulé",
      detail: "Une interaction WhatsApp synthétique traverse ce canal sans envoi externe.",
      durationMs: 130,
    });
  }
  if (node.capabilityKey === "customer.identity.read") {
    return Object.freeze({
      label: "Customer résolu",
      detail: "La résolution d'identité Customer est simulée.",
      durationMs: 90,
    });
  }
  if (node.capabilityKey === "scheduling.appointment") {
    return Object.freeze({
      label: "Scheduling exécuté",
      detail: "Le chemin de réservation est simulé sans créer de rendez-vous réel.",
      durationMs: 160,
    });
  }

  switch (node.kind) {
    case "conversation":
      return Object.freeze({
        label: "Conversation ouverte",
        detail: "Le contexte conversationnel est simulé sans créer de donnée réelle.",
        durationMs: 120,
      });
    case "agent":
      return Object.freeze({
        label: "Agent sollicité",
        detail: "L'orchestration Agent est représentée selon le Blueprint compilé.",
        durationMs: 520,
      });
    case "knowledge":
      return Object.freeze({
        label: "Knowledge consulté",
        detail: "Une consultation Knowledge synthétique traverse ce composant.",
        durationMs: 140,
      });
    case "automation":
      return Object.freeze({
        label: "Automation déclenchée",
        detail: "Une automation synthétique est évaluée sans livraison externe.",
        durationMs: 110,
      });
    case "human":
      return Object.freeze({
        label: "Handoff humain simulé",
        detail: "La frontière de reprise humaine est représentée sans changer de contrôle réel.",
        durationMs: 80,
      });
    case "business":
      return Object.freeze({
        label: node.label + " consulté",
        detail: "Cette capacité métier est simulée à partir du Blueprint compilé.",
        durationMs: 120,
      });
    case "channel":
    default:
      return Object.freeze({
        label: node.label + " simulé",
        detail: "Ce canal est représenté sans exécuter d'action externe.",
        durationMs: 120,
      });
  }
}

function demoTraversal(
  blueprint: SolutionBlueprintV1,
): readonly Readonly<{ nodeKey: SolutionNodeKey; edgeKey?: string }>[] {
  const projection = projectSolutionBlueprint(blueprint);
  if (projection.nodes.length === 0) return Object.freeze([]);

  const incoming = new Map<SolutionNodeKey, number>(
    projection.nodes.map((node) => [node.key, 0]),
  );
  for (const edge of projection.edges) {
    incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
  }

  const roots = projection.nodes
    .filter((node) => (incoming.get(node.key) ?? 0) === 0)
    .map((node) => node.key);
  const queue: Array<{ nodeKey: SolutionNodeKey; edgeKey?: string }> = (
    roots.length > 0 ? roots : [projection.nodes[0]!.key]
  ).map((nodeKey) => ({ nodeKey }));
  const enqueued = new Set<SolutionNodeKey>(queue.map((entry) => entry.nodeKey));
  const visited = new Set<SolutionNodeKey>();
  const traversal: Array<{ nodeKey: SolutionNodeKey; edgeKey?: string }> = [];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current.nodeKey)) continue;
    visited.add(current.nodeKey);
    traversal.push(current);

    for (const edge of projection.edges.filter(
      (candidate) => candidate.from === current.nodeKey,
    )) {
      if (visited.has(edge.to) || enqueued.has(edge.to)) continue;
      enqueued.add(edge.to);
      queue.push({ nodeKey: edge.to, edgeKey: edge.key });
    }
  }

  for (const node of projection.nodes) {
    if (!visited.has(node.key)) traversal.push({ nodeKey: node.key });
  }

  return Object.freeze(traversal.map((entry) => Object.freeze(entry)));
}

export function buildSolutionDemoScenario(
  blueprint: SolutionBlueprintV1,
  runNumber = 1,
): SolutionDemoScenario {
  const validation = validateSolutionBlueprint(blueprint);
  if (!validation.valid) {
    throw new Error(
      "solution_studio_demo_blueprint_invalid:" +
        validation.issues.map((entry) => entry.code).join(","),
    );
  }

  const traversal = demoTraversal(blueprint);
  let offsetMs = 0;
  const correlationId =
    "demo:" + blueprint.templateKey + ":" + Math.max(1, Math.trunc(runNumber));
  const projection = projectSolutionBlueprint(blueprint);
  const nodesByKey = new Map(projection.nodes.map((node) => [node.key, node]));
  const events = traversal.map((entry, index) => {
    const node = nodesByKey.get(entry.nodeKey);
    if (!node) throw new Error("solution_studio_demo_node_missing:" + entry.nodeKey);
    const copy = demoCopyForNode(node);
    const event = Object.freeze({
      eventId: "demo-" + Math.max(1, Math.trunc(runNumber)) + "-" + (index + 1),
      correlationId,
      origin: "simulation" as const,
      phase: index === traversal.length - 1 ? ("completed" as const) : ("started" as const),
      nodeKey: entry.nodeKey,
      source: "solution-demo",
      eventType: "demo." + node.kind,
      durationMs: copy.durationMs,
      edgeKey: entry.edgeKey,
      label: copy.label,
      detail: copy.detail,
      offsetMs,
    });
    offsetMs += copy.durationMs + 360;
    return event;
  });

  return Object.freeze({
    key: "blueprint-flow-v1",
    label: "Parcours généré depuis " + blueprint.displayName,
    input:
      "Interaction client synthétique dérivée du blueprint courant. Aucune action métier ou provider réelle n'est exécutée.",
    result:
      "Flow synthétique terminé · " + events.length + " modules parcourus.",
    events: Object.freeze(events),
  });
}

export function summarizeExecution(
  events: readonly PulseExecutionEvent[],
): SolutionExecutionSummary {
  if (events.length === 0) {
    return Object.freeze({
      eventCount: 0,
      nodeCount: 0,
      failureCount: 0,
      handoffCount: 0,
      totalDurationMs: 0,
      status: "idle",
      resultLabel: null,
    });
  }

  const failures = events.filter((event) => event.phase === "failed").length;
  const handoffs = events.filter((event) => event.phase === "handoff").length;
  const last = events.at(-1)!;
  const lastOffset = last.offsetMs ?? 0;
  const totalDurationMs = events.some((event) => event.offsetMs != null)
    ? lastOffset + (last.durationMs ?? 0)
    : events.reduce((total, event) => total + (event.durationMs ?? 0), 0);

  return Object.freeze({
    eventCount: events.length,
    nodeCount: new Set(events.map((event) => event.nodeKey)).size,
    failureCount: failures,
    handoffCount: handoffs,
    totalDurationMs,
    status: failures > 0
      ? "failed"
      : handoffs > 0
        ? "handoff"
        : last.phase === "completed"
          ? "completed"
          : "running",
    resultLabel: last.label,
  });
}


export interface PulseTraceSummary {
  correlationId: string;
  eventCount: number;
  latestEvent: PulseExecutionEvent;
}

export function runtimeTraceSummaries(
  events: readonly PulseExecutionEvent[],
): readonly PulseTraceSummary[] {
  const runtime = runtimeEventsOnly(events);
  const summaries = new Map<string, PulseTraceSummary>();
  for (const event of runtime) {
    const current = summaries.get(event.correlationId);
    if (current) summaries.delete(event.correlationId);
    summaries.set(event.correlationId, {
      correlationId: event.correlationId,
      eventCount: (current?.eventCount ?? 0) + 1,
      latestEvent: event,
    });
  }
  return Object.freeze([...summaries.values()].reverse());
}

export function eventsForCorrelation(
  events: readonly PulseExecutionEvent[],
  correlationId: string | null,
): readonly PulseExecutionEvent[] {
  if (!correlationId) return [];
  return events.filter((event) => event.correlationId === correlationId);
}

export function runtimeEventsOnly(events: readonly PulseExecutionEvent[]): readonly PulseExecutionEvent[] {
  return events.filter((event) => event.origin === "runtime");
}

export function activeNodeKeys(events: readonly PulseExecutionEvent[]): ReadonlySet<SolutionNodeKey> {
  return new Set(events.slice(-3).map((event) => event.nodeKey));
}

export function activeEdgeKeys(events: readonly PulseExecutionEvent[]): ReadonlySet<string> {
  return new Set(events.slice(-3).flatMap((event) => event.edgeKey ? [event.edgeKey] : []));
}
